import { useEffect, useMemo, useRef, useState } from "react";
import RfidNav from "./RfidNav";
import {
  cardStyle,
  getRfidConfig,
  labelStyle,
  useWarehouseProducts,
  useWarehouses,
} from "./rfidCommon";

// Inventaire RFID : le stock théorique vient du dépôt choisi ; le comptage
// (lecture des étiquettes) est simulé par logiciel avec quelques écarts.

const shuffle = (array) => {
  const a = array.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const buildReadPlan = (products) => {
  const plan = [];
  products.forEach((p) => {
    const r = Math.random();
    let target = p.quantity;
    if (r >= 0.78 && r < 0.95) {
      target = Math.max(0, p.quantity - (1 + Math.floor(Math.random() * 2)));
    } else if (r >= 0.95 && p.quantity > 0) {
      target = p.quantity + 1;
    }
    for (let i = 0; i < target; i++) plan.push(p.id);
  });
  return shuffle(plan);
};

const RfidInventaire = () => {
  const config = useMemo(() => getRfidConfig(), []);
  const { warehouses, loading: loadingWarehouses } = useWarehouses();
  const [warehouseId, setWarehouseId] = useState("");
  const {
    products,
    total,
    loading: loadingProducts,
    error: productsError,
  } = useWarehouseProducts(warehouseId);

  const [readCounts, setReadCounts] = useState({});
  const [reading, setReading] = useState(false);
  const [finished, setFinished] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyGaps, setOnlyGaps] = useState(false);
  const intervalRef = useRef(null);
  const planRef = useRef([]);
  const positionRef = useRef(0);

  const stopReading = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setReading(false);
  };

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const resetInventory = () => {
    stopReading();
    planRef.current = [];
    positionRef.current = 0;
    setReadCounts({});
    setFinished(false);
  };

  const onWarehouseChange = (e) => {
    resetInventory();
    setWarehouseId(e.target.value);
  };

  const startInventory = () => {
    if (intervalRef.current) return;
    if (positionRef.current === 0) {
      planRef.current = buildReadPlan(products);
      setReadCounts({});
      setFinished(false);
    }
    const plan = planRef.current;
    const chunk = Math.max(1, Math.ceil(plan.length / 60));
    setReading(true);
    intervalRef.current = setInterval(() => {
      const next = plan.slice(positionRef.current, positionRef.current + chunk);
      positionRef.current += next.length;
      setReadCounts((old) => {
        const copy = { ...old };
        next.forEach((id) => {
          copy[id] = (copy[id] || 0) + 1;
        });
        return copy;
      });
      if (positionRef.current >= plan.length) {
        stopReading();
        setFinished(true);
      }
    }, config.readIntervalMs);
  };

  const rows = useMemo(
    () =>
      products.map((p) => {
        const read = readCounts[p.id] || 0;
        return { ...p, read, gap: read - p.quantity };
      }),
    [products, readCounts]
  );

  const totals = useMemo(() => {
    const theoretical = rows.reduce((s, r) => s + r.quantity, 0);
    const read = rows.reduce((s, r) => s + r.read, 0);
    const missing = rows.reduce((s, r) => s + (r.gap < 0 ? -r.gap : 0), 0);
    const extra = rows.reduce((s, r) => s + (r.gap > 0 ? r.gap : 0), 0);
    const matching = rows.filter((r) => r.gap === 0).length;
    return {
      theoretical,
      read,
      missing,
      extra,
      conformity: rows.length ? Math.round((matching / rows.length) * 100) : 0,
    };
  }, [rows]);

  const started = reading || finished || positionRef.current > 0;
  const visibleRows = rows.filter((r) => {
    const text = `${r.code} ${r.barcode} ${r.name}`.toLowerCase();
    if (search.trim() && !text.includes(search.trim().toLowerCase()))
      return false;
    if (onlyGaps && (!finished || r.gap === 0)) return false;
    return true;
  });

  const exportCsv = () => {
    const lines = [
      ["Code", "Code à barre", "Libellé", "Théorique", "Lu", "Écart"].join(";"),
      ...rows.map((r) =>
        [r.code, r.barcode, r.name, r.quantity, r.read, r.gap].join(";")
      ),
    ];
    const blob = new Blob(["﻿" + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "inventaire_rfid.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const gapBadge = (r) => {
    if (!started) return <span className="text-muted">-</span>;
    if (!finished && r.read < r.quantity)
      return <span className="badge badge-light border">en cours</span>;
    if (r.gap === 0) return <span className="badge badge-success">conforme</span>;
    if (r.gap < 0)
      return <span className="badge badge-danger">manquant {-r.gap}</span>;
    return <span className="badge badge-warning">en trop {r.gap}</span>;
  };

  const kpis = [
    { label: "Stock théorique", value: totals.theoretical, icon: "fas fa-database" },
    { label: "Articles lus", value: totals.read, icon: "fas fa-tags" },
    { label: "Manquants", value: totals.missing, icon: "fas fa-minus-circle" },
    { label: "En trop", value: totals.extra, icon: "fas fa-plus-circle" },
    {
      label: "Conformité",
      value: finished ? `${totals.conformity}%` : "-",
      icon: "fas fa-check-double",
    },
  ];

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="mb-3">
            <h4 className="mb-1">
              <i className="fas fa-clipboard-check mr-2"></i>Inventaire RFID
            </h4>
            <small className="text-muted">
              Comptez un dépôt entier par lecture des étiquettes et comparez-le
              au stock du système
            </small>
          </div>
          <RfidNav />
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-dark" style={cardStyle}>
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-5">
                  <div className="form-group mb-md-0">
                    <label style={labelStyle}>Dépôt à inventorier</label>
                    <select
                      className="form-control"
                      value={warehouseId}
                      onChange={onWarehouseChange}
                      disabled={loadingWarehouses || reading}
                    >
                      <option value="">
                        {loadingWarehouses ? "Chargement..." : "Choisir..."}
                      </option>
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.label}
                        </option>
                      ))}
                    </select>
                    {warehouseId !== "" && (
                      <small className="text-muted">
                        {loadingProducts
                          ? "Chargement des produits du dépôt..."
                          : productsError
                          ? "Impossible de charger les produits du dépôt"
                          : total > products.length
                          ? `${products.length} produits chargés sur ${total}`
                          : `${products.length} produits`}
                      </small>
                    )}
                  </div>
                </div>
                <div className="col-md-7 text-md-right mt-3 mt-md-0">
                  {reading ? (
                    <button className="btn btn-danger mr-2" onClick={stopReading}>
                      <i className="fas fa-pause mr-1"></i>
                      Mettre en pause
                    </button>
                  ) : (
                    <button
                      className="btn btn-dark mr-2"
                      onClick={startInventory}
                      disabled={products.length === 0 || finished}
                    >
                      <i className="fas fa-play mr-1"></i>
                      {positionRef.current > 0 && !finished
                        ? "Reprendre"
                        : "Démarrer l'inventaire"}
                    </button>
                  )}
                  <button
                    className="btn btn-outline-secondary mr-2"
                    onClick={resetInventory}
                    disabled={!started}
                  >
                    <i className="fas fa-undo mr-1"></i>
                    Réinitialiser
                  </button>
                  <button
                    className="btn btn-outline-dark"
                    onClick={exportCsv}
                    disabled={!finished}
                  >
                    <i className="fas fa-file-csv mr-1"></i>
                    Exporter CSV
                  </button>
                </div>
              </div>
              {reading && (
                <div className="mt-3">
                  <div className="progress" style={{ height: 8 }}>
                    <div
                      className="progress-bar bg-dark"
                      style={{
                        width: `${
                          planRef.current.length
                            ? (positionRef.current / planRef.current.length) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <small className="text-muted">
                    Lecture en cours (lecteur simulé)...
                  </small>
                </div>
              )}
            </div>
          </div>

          <div className="row">
            {kpis.map((k) => (
              <div className="col-lg col-md-4 col-6" key={k.label}>
                <div
                  className="d-flex align-items-center p-3 mb-3 bg-white"
                  style={{
                    ...cardStyle,
                    borderLeft: "4px solid #343a40",
                  }}
                >
                  <span
                    className="d-flex align-items-center justify-content-center bg-dark text-white mr-3"
                    style={{ width: 40, height: 40, borderRadius: 8 }}
                  >
                    <i className={k.icon}></i>
                  </span>
                  <div>
                    <small
                      className="text-muted text-uppercase d-block"
                      style={{ fontWeight: 600, fontSize: "0.7rem" }}
                    >
                      {k.label}
                    </small>
                    <span style={{ fontSize: "1.25rem", fontWeight: 700 }}>
                      {k.value}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card card-outline card-dark" style={cardStyle}>
            <div className="card-header">
              <div className="d-flex flex-wrap justify-content-between align-items-center">
                <h3 className="card-title mb-0">
                  <i className="fas fa-boxes mr-2"></i>Détail par produit
                  <span className="badge badge-dark ml-2">
                    {visibleRows.length}
                  </span>
                </h3>
                <div className="d-flex align-items-center mt-2 mt-sm-0">
                  <div className="custom-control custom-checkbox mr-3">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="onlyGaps"
                      checked={onlyGaps}
                      onChange={(e) => setOnlyGaps(e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="onlyGaps">
                      Écarts seulement
                    </label>
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ width: 220 }}
                    placeholder="Chercher un produit"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="card-body p-0 table-responsive">
              <table className="table table-hover table-striped mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>Code</th>
                    <th>Code à barre</th>
                    <th>Libellé</th>
                    <th className="text-center">Théorique</th>
                    <th className="text-center">Lu</th>
                    <th className="text-center">Écart</th>
                    <th className="text-center">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        <i className="fas fa-clipboard-list fa-2x mb-2 d-block"></i>
                        {warehouseId === ""
                          ? "Choisissez un dépôt pour afficher son stock"
                          : "Aucun produit à afficher"}
                      </td>
                    </tr>
                  )}
                  {visibleRows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <span className="badge badge-light border">
                          {r.code}
                        </span>
                      </td>
                      <td>{r.barcode}</td>
                      <td>
                        <strong>{r.name}</strong>
                      </td>
                      <td className="text-center">{r.quantity}</td>
                      <td className="text-center">{started ? r.read : "-"}</td>
                      <td
                        className="text-center"
                        style={{
                          fontWeight: 600,
                          color:
                            !finished || r.gap === 0
                              ? undefined
                              : r.gap < 0
                              ? "#dc3545"
                              : "#d39e00",
                        }}
                      >
                        {finished ? (r.gap > 0 ? `+${r.gap}` : r.gap) : "-"}
                      </td>
                      <td className="text-center">{gapBadge(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RfidInventaire;
